import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
  useSchwabConfigStatus,
  useSchwabStatus,
  useGetSchwabAuthUrl,
  useSyncSchwabAccount,
  useDisconnectSchwab,
  useRefreshSchwabAccounts,
} from '@/hooks/queries/useSchwab';
import { formatDateTime } from '@/lib/formatters';

export function SchwabConnection() {
  const { data: configStatus, isLoading: configLoading } = useSchwabConfigStatus();
  const { data: status, isLoading: statusLoading } = useSchwabStatus();
  const getAuthUrl = useGetSchwabAuthUrl();
  const syncAccount = useSyncSchwabAccount();
  const disconnect = useDisconnectSchwab();
  const refreshAccounts = useRefreshSchwabAccounts();

  const isLoading = configLoading || statusLoading;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Schwab Connection</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  const isConfigured = configStatus?.configured;
  const isConnected = status?.connected;
  // A reason other than no_tokens means a previous connection stopped working
  const needsReconnect = !isConnected && status?.reason && status.reason !== 'no_tokens';

  const reconnectMessages: Record<string, string> = {
    revoked:
      'Schwab dropped this session — usually because the account was reconnected from another device or browser. Reconnect to resume syncing.',
    expired:
      "The Schwab session reached Schwab's 7-day limit. Reconnect to resume syncing.",
    refresh_failed:
      'Schwab could not be reached to renew the session. This may be temporary — try again shortly, or reconnect.',
  };

  const sessionExpiresAt = status?.refreshTokenExpiresAt ? new Date(status.refreshTokenExpiresAt) : null;
  const sessionHoursLeft = sessionExpiresAt
    ? Math.round((sessionExpiresAt.getTime() - Date.now()) / (60 * 60 * 1000))
    : null;
  const sessionExpiringSoon = sessionHoursLeft !== null && sessionHoursLeft <= 24;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Charles Schwab</CardTitle>
            <CardDescription>Connect your Schwab account to sync trades automatically</CardDescription>
          </div>
          <Badge variant={isConnected ? 'success' : needsReconnect ? 'error' : !isConfigured ? 'warning' : 'default'}>
            {isConnected ? 'Connected' : needsReconnect ? 'Reconnect needed' : !isConfigured ? 'Not Configured' : 'Not Connected'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {!isConnected ? (
          <div className="text-center py-6">
            {!isConfigured ? (
              <>
                <svg
                  className="mx-auto h-12 w-12 text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <h3 className="mt-4 text-sm font-medium text-gray-900">Schwab Integration Not Configured</h3>
                <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                  The Schwab API credentials have not been set up yet. Please configure the SCHWAB_CLIENT_ID,
                  SCHWAB_CLIENT_SECRET, and SCHWAB_REDIRECT_URI secrets to enable this feature.
                </p>
              </>
            ) : (
              <>
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                <h3 className="mt-4 text-sm font-medium text-gray-900">
                  {needsReconnect ? 'Connection lost' : 'No account connected'}
                </h3>
                <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                  {needsReconnect
                    ? reconnectMessages[status!.reason!]
                    : 'Connect your Schwab brokerage account to automatically import your options trades.'}
                </p>
                {status?.email && (
                  <p className="mt-2 text-xs text-gray-400">Signed in as {status.email}</p>
                )}
                <Button
                  className="mt-4"
                  onClick={() => getAuthUrl.mutate()}
                  isLoading={getAuthUrl.isPending}
                >
                  {needsReconnect ? 'Reconnect Schwab Account' : 'Connect Schwab Account'}
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Accounts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">Connected Accounts</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshAccounts.mutate()}
                  isLoading={refreshAccounts.isPending}
                >
                  Refresh Accounts
                </Button>
              </div>
              <div className="space-y-2">
                {status.accounts?.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">No accounts found.</p>
                    <p className="text-xs mt-1">Click "Refresh Accounts" to fetch from Schwab.</p>
                  </div>
                ) : (
                  status.accounts?.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {account.accountName || `Account ${account.accountNumber}`}
                        </p>
                        <p className="text-sm text-gray-500">
                          {account.lastSyncAt ? (
                            <>Last synced: {formatDateTime(account.lastSyncAt)}</>
                          ) : (
                            <span className="text-amber-600">Never synced</span>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => syncAccount.mutate(account.id)}
                        isLoading={syncAccount.isPending}
                      >
                        Sync Now
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Last sync info */}
            {status.lastSync && (
              <p className="text-sm text-gray-500">Last sync: {formatDateTime(status.lastSync)}</p>
            )}

            {/* Session info — Schwab caps sessions at 7 days, so re-auth is always coming */}
            <div className="space-y-1">
              {sessionExpiresAt && (
                sessionExpiringSoon ? (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                    <span className="text-amber-600 text-sm font-medium">
                      Session expires {formatDateTime(status.refreshTokenExpiresAt!)}.
                      Reconnect soon to avoid a sync gap.
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Session valid until {formatDateTime(status.refreshTokenExpiresAt!)} — Schwab
                    requires reconnecting weekly.
                  </p>
                )
              )}
              {status.email && (
                <p className="text-xs text-gray-400">Signed in as {status.email}</p>
              )}
            </div>

            {/* Disconnect */}
            <div className="pt-4 border-t border-gray-200">
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm('Are you sure you want to disconnect your Schwab account?')) {
                    disconnect.mutate();
                  }
                }}
                isLoading={disconnect.isPending}
              >
                Disconnect Account
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
